package com.siriusxm.pia.views.unifiedaggregator.management

import androidx.compose.runtime.*
import com.siriusxm.cmc.utils.toLocalDateTimeString
import com.siriusxm.pia.SXMUI
import com.siriusxm.pia.components.*
import com.siriusxm.pia.rest.unifiedaggregator.FieldDiff
import com.siriusxm.pia.views.unifiedaggregator.AggregatorService
import com.siriusxm.pia.views.unifiedaggregator.DiffFieldChangeList
import com.siriusxm.pia.views.unifiedaggregator.formatFieldName
import unifiedcontent.aggregator.AuditEntry
import unifiedcontent.aggregator.AuditHistoryResult
import unifiedcontent.aggregator.AuditOperation
import unifiedcontent.aggregator.AuditResourceType
import kotlinx.serialization.json.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text

/**
 * Displays the audit history for a producer or consumer resource.
 * Shows a chronological list of all changes made to the resource including
 * who made the change, when it was made, and what type of operation was performed.
 *
 * @param aggregator The aggregator service for API calls
 * @param resourceType The type of resource (PRODUCER or CONSUMER)
 * @param resourceId The ID of the resource to show audit history for
 */
@Composable
fun AuditHistory(
    aggregator: AggregatorService,
    resourceType: AuditResourceType,
    resourceId: String
) {
    var auditResult by remember { mutableStateOf<AuditHistoryResult?>(null) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(resourceId) {
        loading = true
        error = null
        try {
            auditResult = aggregator.clientApi.getResourceAuditHistory(
                resourceType = resourceType,
                resourceId = resourceId,
                limit = 50
            )
        } catch (e: Exception) {
            error = e.message ?: "Failed to load audit history"
        }
        loading = false
    }

    box({
        paddedContent = false
        title = "Change History"
    }) {
        when {
            loading -> {
                boxMessage("Loading audit history...")
            }
            error != null -> {
                boxMessage(error!!)
            }
            auditResult?.entries.isNullOrEmpty() -> {
                boxMessage("No audit history available for this ${resourceType.name.lowercase()}.")
            }
            else -> {
                table<AuditEntry> {
                    items(auditResult?.entries.orEmpty())

                    column {
                        width = 160.px
                        content { entry ->
                            // Date
                            Div({
                                style {
                                    fontSize(13.px)
                                    marginBottom(2.px)
                                }
                            }) {
                                // Render in the globally-selected timezone (header picker),
                                // not the browser's system zone.
                                val timeString = entry.timestamp.toLocalDateTimeIn(currentTimeZoneId()).toLocalDateTimeString()
                                Text(timeString)
                            }
                            // User
                            Div({
                                style {
                                    fontSize(12.px)
                                    color(SXMUI.subtleTextColor.value())
                                }
                            }) {
                                val username = (entry.userEmail ?: entry.userId).substringBefore("@")
                                Text(username)
                            }
                        }
                    }

                    column {
                        content { entry ->
                            // Operation badge
                            Div({
                                style {
                                    marginBottom(8.px)
                                }
                            }) {
                                val (label, color) = entry.operation.displayInfo()
                                Badge(label, color)
                            }
                            AuditDetailsView(entry)
                        }
                    }
                }
            }
        }
    }
}

/**
 * Returns a display label and badge color for an audit operation.
 */
private fun AuditOperation.displayInfo(): Pair<String, BadgeColor> {
    return when (this) {
        AuditOperation.CREATE -> "Created" to BadgeColor.GREEN
        AuditOperation.UPDATE -> "Updated" to BadgeColor.BLUE
        AuditOperation.DELETE -> "Deleted" to BadgeColor.RED
        AuditOperation.ADD_TARGET -> "Target Added" to BadgeColor.GREEN
        AuditOperation.REMOVE_TARGET -> "Target Removed" to BadgeColor.RED
        AuditOperation.ADD_USER -> "User Added" to BadgeColor.GREEN
        AuditOperation.REMOVE_USER -> "User Removed" to BadgeColor.RED
        else -> this.name to BadgeColor.GRAY
    }
}

/**
 * Returns a human-readable description for an audit operation.
 */
private fun AuditOperation.description(): String {
    return when (this) {
        AuditOperation.CREATE -> "Resource was created"
        AuditOperation.UPDATE -> "Configuration was updated"
        AuditOperation.DELETE -> "Resource was deleted"
        AuditOperation.ADD_TARGET -> "Event target was added"
        AuditOperation.REMOVE_TARGET -> "Event target was removed"
        AuditOperation.ADD_USER -> "Service user was added"
        AuditOperation.REMOVE_USER -> "Service user was removed"
        else -> "Unknown operation: ${this.name}"
    }
}

/**
 * Displays the details of an audit entry, showing a diff for UPDATE operations
 * or relevant information for other operation types.
 */
@Composable
private fun AuditDetailsView(entry: AuditEntry) {
    val details = entry.details

    when {
        details == null -> {
            Div({
                style {
                    color(SXMUI.subtleTextColor.value())
                }
            }) {
                Text(entry.operation.description())
            }
        }
        entry.operation == AuditOperation.UPDATE -> {
            // For updates, try to show a diff of before/after
            val jsonObj = details as? JsonObject
            val before = jsonObj?.get("before") as? JsonObject
            val after = jsonObj?.get("after") as? JsonObject

            if (before != null && after != null) {
                DiffView(before, after)
            } else {
                // Fallback if structure is different
                GenericDetailsView(details)
            }
        }
        entry.operation == AuditOperation.ADD_TARGET || entry.operation == AuditOperation.REMOVE_TARGET -> {
            // Show the target ARN or details
            val jsonObj = details as? JsonObject
            val arn = jsonObj?.get("arn")?.jsonPrimitive?.contentOrNull
                ?: jsonObj?.get("target")?.jsonPrimitive?.contentOrNull

            if (arn != null) {
                Div({
                    style {
                        fontSize(13.px)
                        fontFamily("monospace")
                        property("word-break", "break-all")
                    }
                }) {
                    Text(arn)
                }
            } else {
                GenericDetailsView(details)
            }
        }
        entry.operation == AuditOperation.ADD_USER || entry.operation == AuditOperation.REMOVE_USER -> {
            // Show user information
            val jsonObj = details as? JsonObject
            val userId = jsonObj?.get("userId")?.jsonPrimitive?.contentOrNull
                ?: jsonObj?.get("user")?.jsonPrimitive?.contentOrNull
                ?: jsonObj?.get("email")?.jsonPrimitive?.contentOrNull

            if (userId != null) {
                Div({
                    style {
                        fontSize(13.px)
                    }
                }) {
                    Text(userId)
                }
            } else {
                GenericDetailsView(details)
            }
        }
        else -> {
            GenericDetailsView(details)
        }
    }
}

/**
 * Fields to exclude from the diff display (shown elsewhere or redundant).
 */
private val EXCLUDED_DIFF_FIELDS = setOf(
    "lastModifiedBy",
    "lastModified",
    "updatedBy",
    "updatedAt",
    "modifiedBy",
    "modifiedAt"
)

/**
 * Displays a diff between before and after JSON objects,
 * showing only the fields that changed.
 */
@Composable
private fun DiffView(before: JsonObject, after: JsonObject) {
    val allKeys = (before.keys + after.keys).sorted()
        .filterNot { it in EXCLUDED_DIFF_FIELDS }
    val diffs = allKeys.mapNotNull { key ->
        val beforeVal = before[key]
        val afterVal = after[key]
        if (beforeVal != afterVal) {
            FieldDiff(formatFieldName(key), beforeVal, afterVal)
        } else null
    }

    if (diffs.isEmpty()) {
        Div({
            style {
                color(SXMUI.subtleTextColor.value())
            }
        }) {
            Text("No visible changes")
        }
    } else {
        DiffFieldChangeList(diffs)
    }
}

/**
 * Generic fallback view for displaying JSON details.
 */
@Composable
private fun GenericDetailsView(details: JsonElement) {
    Div({
        style {
            fontSize(12.px)
            color(SXMUI.subtleTextColor.value())
            fontFamily("monospace")
            property("word-break", "break-all")
            maxWidth(400.px)
        }
    }) {
        Text(details.toString().take(300))
    }
}

